import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import { ApiError, createCompanyWorkflowRequest, trackCompany } from "../lib/api";
import { companyReadinessQueryOptions } from "../lib/companyQueries";
import {
  fetchCompanyWorkspace,
  updateCompanyWorkspaceGap,
  updateCompanyWorkspaceTier,
  type CompanyWorkspaceCollection,
  type CompanyWorkspaceGap,
  type CompanyWorkspaceMonitor,
  type CompanyWorkspaceOperationalStatus,
  type CompanyWorkspacePosition,
  type CompanyWorkspaceResearchArtifact,
  type CompanyWorkspaceResponse,
  type CompanyWorkspaceTier,
} from "../lib/companyWorkspace";
import { useCan } from "../lib/session";
import type { ChipTone } from "../lib/types";
import "../styles/company-workspace.css";

const TIERS: CompanyWorkspaceTier[] = ["portfolio", "active_bench", "universe"];

function appPath(path: string): string {
  return path.startsWith("/app/") ? path.slice(4) : path;
}

function humanize(value: string | null | undefined): string {
  if (!value) return "Unavailable";
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatWhen(value: string | null | undefined, dateOnly = false): string {
  if (!value) return "Unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, dateOnly
    ? { dateStyle: "medium" }
    : { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function When({ value, dateOnly = false }: { value: string | null | undefined; dateOnly?: boolean }) {
  if (!value) return <>Unavailable</>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <>Date unavailable</>;
  return <time dateTime={value}>{formatWhen(value, dateOnly)}</time>;
}

function formatWeight(value: number | null): string {
  return value === null ? "Not set" : `${(value / 100).toFixed(2)}%`;
}

function StateMessage({
  title,
  detail,
  action,
  error = false,
  status = false,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
  error?: boolean;
  status?: boolean;
}) {
  return (
    <div
      className={`company-workspace-state${error ? " company-workspace-state--error" : ""}`}
      role={error ? "alert" : status ? "status" : undefined}
    >
      <strong>{title}</strong>
      <span>{detail}</span>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function CollectionCount<T>({ collection }: { collection: CompanyWorkspaceCollection<T> }) {
  if (collection.total_count <= collection.shown_count) return null;
  return (
    <span className="company-workspace-bounded-note">
      Showing {collection.shown_count.toLocaleString()} of {collection.total_count.toLocaleString()}.
    </span>
  );
}

function researchStatus(status: string): { label: string; tone: ChipTone } {
  if (status === "approved") return { label: "Approved", tone: "green" };
  if (status === "review_required" || status === "pending_review") {
    return { label: "Review required", tone: "amber" };
  }
  if (status === "rejected") return { label: "Rejected", tone: "red" };
  if (status === "draft") return { label: "Draft", tone: "gray" };
  return { label: humanize(status), tone: "blue" };
}

function signalPresentation(monitor: CompanyWorkspaceMonitor): { label: string; tone: ChipTone } {
  if (monitor.rule_needs_evaluation) return { label: "Untested · updated rule", tone: "gray" };
  if (monitor.signal_state === "tripped") return { label: "Needs review", tone: "red" };
  if (monitor.signal_state === "watching") return { label: "Watching", tone: "amber" };
  if (monitor.signal_state === "ok") {
    return monitor.monitor_kind === "key_signal"
      ? { label: "Rule passing", tone: "green" }
      : { label: "No breach found", tone: "green" };
  }
  return { label: "Untested", tone: "gray" };
}

function operationalPresentation(status: CompanyWorkspaceOperationalStatus): { label: string; tone: ChipTone } {
  if (status === "evaluating") return { label: "Checking now", tone: "blue" };
  if (status === "pending") return { label: "Queued for check", tone: "blue" };
  if (status === "waiting") return { label: "Waiting for evidence", tone: "gray" };
  if (status === "error") return { label: "Check issue", tone: "red" };
  if (status === "paused") return { label: "Paused", tone: "gray" };
  if (status === "archived") return { label: "Archived", tone: "gray" };
  if (status === "untested") return { label: "Not checked yet", tone: "gray" };
  return { label: "Active", tone: "blue" };
}

function monitorAttentionPresentation(monitor: CompanyWorkspaceMonitor): {
  label: string;
  detail: string;
  tone: ChipTone;
} | null {
  if (!monitor.needs_attention) return null;
  if (monitor.rule_needs_evaluation) {
    return {
      label: "Updated rule awaiting evaluation",
      detail: "The prior assessment does not apply to the updated rule.",
      tone: "amber",
    };
  }
  if (
    monitor.operational_status === "error"
    || monitor.last_attempt_outcome === "configuration_error"
    || monitor.last_attempt_outcome === "failed"
  ) {
    return {
      label: "Check issue",
      detail: "Athena could not complete the latest check. Open the monitor for safe setup details.",
      tone: "red",
    };
  }
  if (monitor.last_attempt_outcome === "no_fact") {
    return {
      label: "Matching fact unavailable",
      detail: "The fact required by this rule has not been stored yet.",
      tone: "amber",
    };
  }
  if (monitor.last_attempt_outcome === "stale_fact") {
    return {
      label: "Matching fact is stale",
      detail: "The latest matching fact is outside its supported freshness window.",
      tone: "amber",
    };
  }
  const signal = signalPresentation(monitor);
  return {
    label: signal.label,
    detail: monitor.signal_summary || "The latest assessment needs evidence review.",
    tone: signal.tone === "gray" ? "amber" : signal.tone,
  };
}

function ResearchArtifactLink({
  artifact,
  label,
}: {
  artifact: CompanyWorkspaceResearchArtifact;
  label: string;
}) {
  const status = researchStatus(artifact.status);
  const approvedArtifact = artifact.status === "approved" && artifact.approved_at;
  return (
    <div className="company-research-artifact">
      <span className="company-workspace-kicker">{label}</span>
      <div className="company-workspace-row-title">
        <Link to={appPath(artifact.deep_link)}>{artifact.title || humanize(artifact.artifact_type)}</Link>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </div>
      <span
        className="company-workspace-meta"
        aria-label={approvedArtifact ? `Approved ${formatWhen(artifact.approved_at)}` : undefined}
      >
        {approvedArtifact ? "Approved " : "Updated "}
        <When value={approvedArtifact ? artifact.approved_at : artifact.updated_at ?? artifact.created_at} />
      </span>
    </div>
  );
}

function ResearchPanel({ workspace }: { workspace: CompanyWorkspaceResponse }) {
  const collection = workspace.research;
  return (
    <Surface className="company-workspace-surface company-workspace-research" aria-labelledby="company-research-title">
      <SurfaceHeader
        title="Research history"
        titleId="company-research-title"
        count={collection.total_count}
        detail="One row per research type; the newest run stays distinct from the approved baseline."
        action={<Link to="/research">All research</Link>}
      />
      {workspace.company.tracked && !workspace.sections.research.available ? (
        <StateMessage
          title="Research history temporarily unavailable"
          detail="Athena could not read the stored research schema for this snapshot. This is not an empty research history."
        />
      ) : collection.items.length ? (
        <div className="company-research-list">
          {collection.items.map((series) => {
            const approved = series.latest_approved_artifact;
            const approvedIsLatest = approved?.artifact_id === series.latest_artifact.artifact_id;
            return (
              <article className="company-research-series" key={series.artifact_type}>
                <header>
                  <div>
                    <h3>{humanize(series.artifact_type)}</h3>
                    <span>{series.run_count.toLocaleString()} {series.run_count === 1 ? "run" : "runs"}</span>
                  </div>
                  {series.latest_requires_review ? <StatusBadge tone="amber">Needs review</StatusBadge> : null}
                </header>
                <ResearchArtifactLink artifact={series.latest_artifact} label="Latest run" />
                {approved && !approvedIsLatest ? (
                  <ResearchArtifactLink artifact={approved} label="Latest approved baseline" />
                ) : null}
                {!approved ? (
                  <p className="company-workspace-inline-note">No approved baseline is stored for this research type.</p>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <StateMessage
          title={workspace.company.tracked ? "No research outputs yet" : "No Athena research"}
          detail={workspace.company.tracked
            ? "No grouped research series are stored for this company."
            : "This provider directory profile is not tracked, so it has no Athena research history."}
        />
      )}
      {workspace.sections.research.available ? <CollectionCount collection={collection} /> : null}
    </Surface>
  );
}

function DocumentsPanel({ workspace }: { workspace: CompanyWorkspaceResponse }) {
  const { documents, company } = workspace;
  return (
    <Surface className="company-workspace-surface company-workspace-documents" aria-labelledby="company-documents-title">
      <SurfaceHeader
        title="Recent documents"
        titleId="company-documents-title"
        count={documents.total_count}
        detail="Newest indexed company evidence, with source and document date."
        action={<Link to="/documents">All documents</Link>}
      />
      {company.tracked && !workspace.sections.documents.available ? (
        <StateMessage
          title="Documents temporarily unavailable"
          detail="Athena could not read the company document index for this snapshot. This is not an empty document feed."
        />
      ) : documents.items.length ? (
        <ol className="company-document-list">
          {documents.items.map((document) => (
            <li key={document.document_id}>
              <div className="company-workspace-row-title">
                <Link to={appPath(document.deep_link)}>{document.title || "Untitled document"}</Link>
                {document.doc_type ? <StatusBadge tone="gray">{humanize(document.doc_type)}</StatusBadge> : null}
              </div>
              <div className="company-document-meta">
                <span>{document.source_name || document.provider || "Source unavailable"}</span>
                {document.document_date ? (
                  <span><When value={document.document_date} dateOnly /></span>
                ) : document.ingested_at ? (
                  <span aria-label={`Ingested ${formatWhen(document.ingested_at, true)}`}>
                    Ingested <When value={document.ingested_at} dateOnly />
                  </span>
                ) : (
                  <span>Date unavailable</span>
                )}
                {document.page_count !== null ? <span>{document.page_count} pp</span> : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <StateMessage
          title={company.tracked ? "No indexed documents" : "No Athena documents"}
          detail={company.tracked
            ? "No documents are present in this company workspace snapshot."
            : "This directory profile is not tracked, so Athena has no company document feed."}
        />
      )}
      {workspace.sections.documents.available ? <CollectionCount collection={documents} /> : null}
    </Surface>
  );
}

function tierChangeExplanation(current: CompanyWorkspaceTier | null, next: CompanyWorkspaceTier): string {
  if (current === null && next === "portfolio") {
    return "This will create Athena research coverage, start the Portfolio monitoring cadence, and create, reactivate, or synchronize its core-book position. This form does not set a position weight.";
  }
  if (current === null) {
    return `This will create Athena research coverage at ${humanize(next)} and start its ${humanize(next)} monitoring cadence. It does not create a core-book position or change an investment view.`;
  }
  if (current !== "portfolio" && next === "portfolio") {
    return "Moving this company to Portfolio will create, reactivate, or synchronize its core-book position. This form does not set a position weight.";
  }
  if (current === "portfolio" && next !== "portfolio") {
    return "Moving this company out of Portfolio will exit any live core-book position. Any already-running research work may still finish.";
  }
  return "This changes the company’s research coverage tier and its monitoring cadence. It does not change an investment view.";
}

function TierControl({ workspace, identifier }: { workspace: CompanyWorkspaceResponse; identifier: string }) {
  const coverage = workspace.coverage;
  const ticker = workspace.company.canonical_ticker;
  const issuerId = workspace.company.issuer_id;
  const queryClient = useQueryClient();
  const [nextTier, setNextTier] = useState<CompanyWorkspaceTier>(coverage?.tier ?? "universe");
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setNextTier(coverage?.tier ?? "universe");
  }, [coverage?.tier]);

  const restoreFocus = useCallback(() => {
    window.setTimeout(() => trigger.current?.focus({ preventScroll: true }));
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    restoreFocus();
  }, [restoreFocus]);

  const save = useMutation({
    mutationFn: () => updateCompanyWorkspaceTier(ticker as string, {
      issuer_id: issuerId as string,
      tier: nextTier,
    }),
    onSuccess: () => {
      setOpen(false);
      restoreFocus();
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["company-workspace", identifier] });
    },
  });

  useEffect(() => {
    if (!open) return;
    cancel.current?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!save.isPending) closeDialog();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>(
        "button:not(:disabled), select:not(:disabled), [href], [tabindex]:not([tabindex='-1'])",
      ) ?? []);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeDialog, open, save.isPending]);

  if (!ticker || !issuerId) return null;
  return (
    <div className="company-tier-control">
      <label htmlFor="company-coverage-tier">Coverage tier</label>
      <div>
        <select
          id="company-coverage-tier"
          value={nextTier}
          onChange={(event) => setNextTier(event.target.value as CompanyWorkspaceTier)}
        >
          {TIERS.map((tier) => <option key={tier} value={tier}>{humanize(tier)}</option>)}
        </select>
        <button
          ref={trigger}
          className="btn"
          type="button"
          disabled={coverage ? nextTier === coverage.tier : false}
          onClick={() => setOpen(true)}
        >
          {coverage ? "Review tier change" : "Add to coverage"}
        </button>
      </div>
      {open ? (
        <div
          className="company-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !save.isPending) closeDialog();
          }}
        >
          <div
            ref={dialog}
            className="company-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="company-tier-dialog-title"
            aria-describedby="company-tier-dialog-copy"
          >
            <span className="company-workspace-kicker">
              {coverage ? "Coverage tier change" : "New research coverage"}
            </span>
            <h2 id="company-tier-dialog-title">
              {coverage ? `Move to ${humanize(nextTier)}?` : `Add to ${humanize(nextTier)} coverage?`}
            </h2>
            <p id="company-tier-dialog-copy">
              {tierChangeExplanation(coverage?.tier ?? null, nextTier)}
              {coverage && coverage.status !== "active"
                ? ` Coverage status remains ${humanize(coverage.status)}; this tier change does not resume it.`
                : ""}
            </p>
            <dl>
              <div><dt>Company</dt><dd>{ticker} · {workspace.company.company_name}</dd></div>
              <div><dt>Current tier</dt><dd>{coverage ? humanize(coverage.tier) : "Not in coverage"}</dd></div>
              <div><dt>New tier</dt><dd>{humanize(nextTier)}</dd></div>
            </dl>
            {save.isError ? (
              <div className="company-inline-error" role="alert">
                Athena could not confirm the tier change. Refresh before retrying.
              </div>
            ) : null}
            <div className="company-dialog-actions">
              <button ref={cancel} className="btn" type="button" disabled={save.isPending} onClick={closeDialog}>Cancel</button>
              <button className="btn primary" type="button" disabled={save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : coverage ? "Confirm tier change" : "Confirm add to coverage"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PositionSummary({ position }: { position: CompanyWorkspacePosition | null }) {
  if (!position) {
    return <p className="company-workspace-inline-note">No active core-book position is returned for this company.</p>;
  }
  return (
    <dl className="company-position-facts">
      <div><dt>Book</dt><dd>{position.book_name || "Core portfolio"}</dd></div>
      <div><dt>Side</dt><dd>{humanize(position.side)}</dd></div>
      <div><dt>Current weight</dt><dd>{formatWeight(position.weight_bps)}</dd></div>
      <div><dt>Target weight</dt><dd>{formatWeight(position.target_weight_bps)}</dd></div>
      <div><dt>Status</dt><dd>{humanize(position.status)}</dd></div>
      <div><dt>Updated</dt><dd><When value={position.updated_at} /></dd></div>
    </dl>
  );
}

function sourceStatusTone(status: string): ChipTone {
  if (["ready", "available", "complete", "current"].includes(status)) return "green";
  if (["queued", "refresh_pending", "pending"].includes(status)) return "amber";
  if (["acquiring", "running", "refreshing"].includes(status)) return "blue";
  if (["attention", "failed", "missing", "missing_identifier", "stale", "quality_issues"].includes(status)) return "red";
  return "gray";
}

export function SourceCoveragePanel({ companyRef }: { companyRef: string }) {
  const readiness = useQuery(companyReadinessQueryOptions(companyRef));
  const acquisition = readiness.data?.acquisition;
  const snapshot = readiness.data?.readiness;
  const checks = snapshot?.source_checks ?? [];
  const acquisitionStatus = acquisition?.status ?? "not_started";
  const activeCaptureCount = typeof acquisition?.active_capture_count === "number"
    ? acquisition.active_capture_count
    : 0;
  const failedCaptureCount = typeof acquisition?.failed_capture_count === "number"
    ? acquisition.failed_capture_count
    : 0;
  const activeWorkCount = (acquisition?.active_job_count ?? 0) + activeCaptureCount;
  const failedWorkCount = (acquisition?.failed_job_count ?? 0) + failedCaptureCount;

  return (
    <Surface className="company-workspace-surface source-coverage-panel" aria-labelledby="source-coverage-heading">
      <SurfaceHeader
        title="Source coverage"
        titleId="source-coverage-heading"
        detail="Acquisition activity and the latest bounded readiness checks."
      />
      {readiness.isLoading ? <StateMessage title="Checking source coverage" detail="Loading acquisition and readiness state…" status /> : null}
      {readiness.isError ? <StateMessage title="Source coverage unavailable" detail="Athena could not retrieve the current readiness snapshot." error /> : null}
      {readiness.data ? (
        <>
          <div className="source-coverage-head">
            <span>Acquisition</span>
            <StatusBadge tone={sourceStatusTone(acquisitionStatus)}>{humanize(acquisitionStatus)}</StatusBadge>
          </div>
          <div className="source-coverage-metrics">
            <span><b>{checks.length}</b> source checks</span>
            <span><b>{activeWorkCount}</b> active tasks</span>
            {failedWorkCount > 0 ? <span aria-label={`${failedWorkCount} failed tasks`}><b>{failedWorkCount}</b> failed tasks</span> : null}
          </div>
          {checks.length ? (
            <div className="source-check-list">
              {checks.slice(0, 5).map((check, index) => (
                <div className="source-check" key={`${check.source}-${index}`}>
                  <span>{check.label || check.source}</span>
                  <span className="source-check-state">
                    {typeof check.document_count === "number" ? <span>{check.document_count} docs</span> : null}
                    <StatusBadge tone={sourceStatusTone(check.status)}>{humanize(check.status)}</StatusBadge>
                  </span>
                </div>
              ))}
              {checks.length > 5 ? <div className="company-workspace-inline-note">+{checks.length - 5} more source checks</div> : null}
            </div>
          ) : (
            <p className="company-workspace-inline-note">
              {snapshot
                ? "No source checks were recorded in the latest readiness snapshot."
                : acquisitionStatus === "queued" || acquisitionStatus === "acquiring"
                  ? "Source acquisition is in progress."
                  : "No source readiness snapshot has been generated yet."}
            </p>
          )}
          {snapshot?.readiness_status ? (
            <div className="source-coverage-foot">
              Readiness: <strong>{humanize(snapshot.readiness_status)}</strong>
              {snapshot.generated_at ? <> · Updated <When value={snapshot.generated_at} /></> : null}
            </div>
          ) : acquisition?.latest_activity_at ? (
            <div className="source-coverage-foot">Latest activity <When value={acquisition.latest_activity_at} /></div>
          ) : null}
        </>
      ) : null}
    </Surface>
  );
}

export function CompanyOnboardingActions({
  identifier,
  canFetchSources,
}: {
  identifier: string;
  canFetchSources: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onboarding = useMutation({
    mutationFn: async ({ fetchSources }: { fetchSources: boolean }) => {
      const tracked = await trackCompany(identifier, { tier: "universe" });
      const canonicalIssuerId = tracked.company.issuer_id.trim();
      if (!canonicalIssuerId) throw new Error("The company was added, but no canonical issuer ID was returned.");
      let refreshProblem: string | null = null;
      if (fetchSources) {
        try {
          await createCompanyWorkflowRequest(canonicalIssuerId, {
            request_type: "refresh_all_sources",
            depth: "quick_refresh",
          });
        } catch (error) {
          refreshProblem = error instanceof Error ? error.message : "The source refresh could not be queued.";
        }
      }
      return { canonicalIssuerId, refreshDispatched: fetchSources && !refreshProblem, refreshProblem };
    },
    onSuccess: async ({ canonicalIssuerId, refreshDispatched, refreshProblem }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["company-workspace"] }),
        queryClient.invalidateQueries({ queryKey: ["companies"] }),
        queryClient.invalidateQueries({ queryKey: ["company-search"] }),
        queryClient.invalidateQueries({ queryKey: ["company-documents"] }),
        ...(refreshDispatched ? [queryClient.invalidateQueries({ queryKey: ["company-readiness"] })] : []),
      ]);
      void navigate(`/companies/${encodeURIComponent(canonicalIssuerId)}`, {
        replace: true,
        state: refreshProblem
          ? { companyOnboardingNotice: `Added to coverage, but source fetching could not start: ${refreshProblem}` }
          : null,
      });
    },
  });

  return (
    <div className="coverage-onboarding">
      <p>Add this company to Athena coverage to retain research, evidence, and workflow history.</p>
      <div className="coverage-onboarding-actions">
        <button className="btn" type="button" disabled={onboarding.isPending} onClick={() => onboarding.mutate({ fetchSources: false })}>
          {onboarding.isPending && !onboarding.variables?.fetchSources ? "Adding…" : "Add to coverage"}
        </button>
        {canFetchSources ? (
          <button className="btn primary" type="button" disabled={onboarding.isPending} onClick={() => onboarding.mutate({ fetchSources: true })}>
            {onboarding.isPending && onboarding.variables?.fetchSources ? "Adding & fetching…" : "Add & fetch sources"}
          </button>
        ) : null}
      </div>
      {onboarding.isError ? <p className="company-workspace-inline-note" role="alert">{(onboarding.error as Error).message}</p> : null}
    </div>
  );
}

function CoveragePanel({
  workspace,
  identifier,
  canReadPosition,
  canWritePortfolio,
  canWriteResearch,
  canRunWorkflows,
}: {
  workspace: CompanyWorkspaceResponse;
  identifier: string;
  canReadPosition: boolean;
  canWritePortfolio: boolean;
  canWriteResearch: boolean;
  canRunWorkflows: boolean;
}) {
  const { coverage, company, sections } = workspace;
  return (
    <Surface className="company-workspace-surface company-coverage-panel" aria-labelledby="company-coverage-title">
      <SurfaceHeader title="Coverage & position" titleId="company-coverage-title" />
      {company.tracked && !sections.coverage.available ? (
        <StateMessage
          title="Coverage context temporarily unavailable"
          detail="Athena could not read the coverage schema for this snapshot. This does not mean the company has no active coverage tier."
        />
      ) : coverage ? (
        <>
          <dl className="company-coverage-facts">
            <div><dt>Tier</dt><dd><StatusBadge tone={coverage.tier === "portfolio" ? "green" : "blue"}>{humanize(coverage.tier)}</StatusBadge></dd></div>
            <div><dt>Status</dt><dd>{humanize(coverage.status)}</dd></div>
            <div><dt>Priority</dt><dd>{humanize(coverage.priority)}</dd></div>
            <div><dt>Owner</dt><dd>{coverage.owner || "Unassigned"}</dd></div>
            <div className="company-fact-wide"><dt>Coverage rationale</dt><dd>{coverage.rationale || "No rationale recorded."}</dd></div>
            <div className="company-fact-wide"><dt>Coverage updated</dt><dd><When value={coverage.updated_at} /></dd></div>
          </dl>
          {canWritePortfolio && company.tracked ? <TierControl workspace={workspace} identifier={identifier} /> : null}
        </>
      ) : (
        <>
          <StateMessage
            title={company.tracked ? "No active coverage tier" : "Not tracked by Athena"}
            detail={company.tracked
              ? "Athena recognizes this issuer, but no active research tier is returned."
              : "This provider directory profile has not been added to Athena. Opening it did not create coverage, a position, a job, or model spend."}
          />
          {canWritePortfolio && company.tracked ? (
            <TierControl workspace={workspace} identifier={identifier} />
          ) : null}
          {!company.tracked && canWriteResearch ? (
            <CompanyOnboardingActions identifier={identifier} canFetchSources={canRunWorkflows} />
          ) : null}
        </>
      )}
      {company.tracked && canReadPosition && sections.position.authorized ? (
        <div className="company-position-block">
          <div className="company-workspace-subhead">
            <h3>Core-book position</h3>
            <Link to="/portfolio">Open portfolio</Link>
          </div>
          {!sections.position.available ? (
            <p className="company-workspace-inline-note">Position context is temporarily unavailable.</p>
          ) : sections.position.included ? (
            <PositionSummary position={workspace.position ?? null} />
          ) : (
            <p className="company-workspace-inline-note">Position context was not included in this snapshot.</p>
          )}
        </div>
      ) : null}
    </Surface>
  );
}

function MonitorPanel({
  workspace,
  canReadMonitors,
}: {
  workspace: CompanyWorkspaceResponse;
  canReadMonitors: boolean;
}) {
  const section = workspace.sections.monitors;
  const ticker = workspace.company.canonical_ticker ?? workspace.company.display_ticker ?? "";
  if (!workspace.company.tracked || !canReadMonitors || !section.authorized) return null;
  const monitors = workspace.monitors;
  return (
    <Surface className="company-workspace-surface company-monitors-panel" aria-labelledby="company-monitors-title">
      <SurfaceHeader
        title="Thesis monitors"
        titleId="company-monitors-title"
        count={monitors?.total_count}
        detail="Investment signal and operational state are shown separately."
        action={<Link to={`/monitors?view=all&q=${encodeURIComponent(ticker)}`}>Open monitors</Link>}
      />
      {!section.available ? (
        <StateMessage title="Monitor status unavailable" detail="Athena could not load company monitors for this snapshot." error />
      ) : !section.included ? (
        <StateMessage title="Monitor status not included" detail="This snapshot did not include monitor data." />
      ) : monitors?.items.length ? (
        <div className="company-monitor-list">
          {monitors.items.map((monitor) => {
            const signal = signalPresentation(monitor);
            const operational = operationalPresentation(monitor.operational_status);
            const attention = monitorAttentionPresentation(monitor);
            return (
              <article key={monitor.monitor_id}>
                <div className="company-workspace-row-title">
                  <Link to={appPath(monitor.deep_link)}>{monitor.name}</Link>
                  <span className="company-monitor-kind">{monitor.monitor_kind === "key_signal" ? "Key signal" : "Thesis check"}</span>
                </div>
                <div className="company-monitor-badges">
                  <StatusBadge tone={signal.tone}>
                    <span className="company-workspace-visually-hidden">Signal: </span>{signal.label}
                  </StatusBadge>
                  <StatusBadge tone={operational.tone}>
                    <span className="company-workspace-visually-hidden">Operations: </span>{operational.label}
                  </StatusBadge>
                </div>
                {attention && attention.label !== signal.label ? (
                  <p className="company-monitor-attention"><strong>{attention.label}.</strong> {attention.detail}</p>
                ) : null}
                <p>{monitor.signal_summary || "No current assessment summary is stored."}</p>
                <span className="company-workspace-meta">Last assessed <When value={monitor.last_evaluated_at} /></span>
              </article>
            );
          })}
        </div>
      ) : (
        <StateMessage title="No monitors configured" detail="No thesis checks or key-signal rules are attached to this company." />
      )}
      {monitors ? <CollectionCount collection={monitors} /> : null}
      <p className="company-workspace-footnote">A signal prompts evidence review; it does not determine thesis health or change a position.</p>
    </Surface>
  );
}

function GapRow({
  gap,
  ticker,
  issuerId,
  identifier,
  editable,
  onDismissed,
}: {
  gap: CompanyWorkspaceGap;
  ticker: string;
  issuerId: string;
  identifier: string;
  editable: boolean;
  onDismissed: () => void;
}) {
  const queryClient = useQueryClient();
  const [dismissOpen, setDismissOpen] = useState(false);
  const dismissTrigger = useRef<HTMLButtonElement>(null);
  const dismissDialog = useRef<HTMLDivElement>(null);
  const dismissCancel = useRef<HTMLButtonElement>(null);
  const restoreDismissFocus = useCallback(() => {
    window.setTimeout(() => dismissTrigger.current?.focus({ preventScroll: true }));
  }, []);
  const closeDismiss = useCallback(() => {
    setDismissOpen(false);
    restoreDismissFocus();
  }, [restoreDismissFocus]);
  const update = useMutation({
    mutationFn: (status: "resolved" | "dismissed") => updateCompanyWorkspaceGap(ticker, {
      issuer_id: issuerId,
      gaps: [{ gap_id: gap.gap_id, status }],
    }),
    onSuccess: (_result, status) => {
      if (status === "dismissed") {
        setDismissOpen(false);
        onDismissed();
      }
    },
    onSettled: () => { void queryClient.invalidateQueries({ queryKey: ["company-workspace", identifier] }); },
  });

  useEffect(() => {
    if (dismissOpen) dismissCancel.current?.focus({ preventScroll: true });
  }, [dismissOpen]);

  useEffect(() => {
    if (!dismissOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        if (!update.isPending) closeDismiss();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dismissDialog.current?.querySelectorAll<HTMLElement>(
        "button:not(:disabled), [href], [tabindex]:not([tabindex='-1'])",
      ) ?? []);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [closeDismiss, dismissOpen, update.isPending]);

  return (
    <li className={gap.needs_attention ? "company-gap--attention" : ""}>
      <div className="company-workspace-row-title">
        <strong>{gap.gap_text}</strong>
        <StatusBadge tone={gap.needs_attention ? "red" : "gray"}>{humanize(gap.severity)}</StatusBadge>
      </div>
      <span className="company-workspace-meta">
        {[gap.gap_category ? humanize(gap.gap_category) : null, humanize(gap.status)].filter(Boolean).join(" · ")}
        {gap.updated_at ? <> · Updated <When value={gap.updated_at} /></> : null}
      </span>
      {editable ? (
        <div className="company-gap-actions">
          <button
            className="btn small"
            type="button"
            disabled={update.isPending}
            aria-label={`Mark ${gap.gap_text} resolved`}
            onClick={() => update.mutate("resolved")}
          >
            Resolve
          </button>
          <button
            ref={dismissTrigger}
            className="btn small"
            type="button"
            disabled={update.isPending}
            aria-label={`Dismiss ${gap.gap_text}`}
            onClick={() => {
              update.reset();
              setDismissOpen(true);
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {update.isError && update.variables === "resolved" ? (
        <span className="company-inline-error" role="alert">
          Athena could not confirm the evidence-gap change. Refresh before retrying.
        </span>
      ) : null}
      {dismissOpen ? (
        <div
          className="company-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !update.isPending) closeDismiss();
          }}
        >
          <div
            ref={dismissDialog}
            className="company-confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={`company-gap-dismiss-title-${gap.gap_id}`}
            aria-describedby={`company-gap-dismiss-copy-${gap.gap_id}`}
          >
            <span className="company-workspace-kicker">Evidence-gap status</span>
            <h2 id={`company-gap-dismiss-title-${gap.gap_id}`}>Dismiss this evidence gap?</h2>
            <p id={`company-gap-dismiss-copy-${gap.gap_id}`}>
              Dismissing “{gap.gap_text}” removes it from active evidence gaps. It does not assert that the missing evidence was resolved.
            </p>
            {update.isError ? (
              <div className="company-inline-error" role="alert">
                Athena could not confirm the evidence-gap change. Refresh before retrying.
              </div>
            ) : null}
            <div className="company-dialog-actions">
              <button
                ref={dismissCancel}
                className="btn"
                type="button"
                disabled={update.isPending}
                onClick={closeDismiss}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                type="button"
                disabled={update.isPending}
                onClick={() => update.mutate("dismissed")}
              >
                {update.isPending ? "Dismissing…" : "Confirm dismiss"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function GapsPanel({
  workspace,
  identifier,
  editable,
}: {
  workspace: CompanyWorkspaceResponse;
  identifier: string;
  editable: boolean;
}) {
  const ticker = workspace.company.canonical_ticker;
  const issuerId = workspace.company.issuer_id;
  const canEdit = editable && workspace.company.tracked && Boolean(ticker && issuerId);
  const focusRegionAfterDismiss = useCallback(() => {
    window.setTimeout(() => {
      document.getElementById("company-gaps-region")?.focus({ preventScroll: true });
    });
  }, []);
  return (
    <Surface
      id="company-gaps-region"
      className="company-workspace-surface company-gaps-panel"
      aria-labelledby="company-gaps-title"
      tabIndex={-1}
    >
      <SurfaceHeader
        title="Evidence gaps"
        titleId="company-gaps-title"
        count={workspace.gaps.total_count}
        detail="Missing or unresolved evidence; high-severity open items feed the attention view."
      />
      {!workspace.sections.gaps.available ? (
        <StateMessage
          title="Evidence gaps temporarily unavailable"
          detail="Athena could not read the evidence-gap schema for this snapshot. This is not confirmation that no open gaps exist."
        />
      ) : workspace.gaps.items.length ? (
        <ul className="company-gap-list">
          {workspace.gaps.items.map((gap) => (
            <GapRow
              key={gap.gap_id}
              gap={gap}
              ticker={ticker as string}
              issuerId={issuerId as string}
              identifier={identifier}
              editable={canEdit}
              onDismissed={focusRegionAfterDismiss}
            />
          ))}
        </ul>
      ) : (
        <StateMessage title="No open evidence gaps" detail="No open or selected evidence gaps are returned in this snapshot." />
      )}
      {workspace.sections.gaps.available ? <CollectionCount collection={workspace.gaps} /> : null}
    </Surface>
  );
}

interface AttentionItem {
  id: string;
  title: string;
  detail: string;
  href: string | null;
  tone: ChipTone;
}

function attentionItems(workspace: CompanyWorkspaceResponse): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const monitor of workspace.monitors?.items ?? []) {
    const attention = monitorAttentionPresentation(monitor);
    if (!attention) continue;
    items.push({
      id: `monitor-${monitor.monitor_id}`,
      title: `${monitor.name} · ${attention.label}`,
      detail: attention.detail,
      href: appPath(monitor.deep_link),
      tone: attention.tone,
    });
  }
  for (const series of workspace.research.items) {
    if (!series.latest_requires_review) continue;
    items.push({
      id: `research-${series.artifact_type}`,
      title: `${humanize(series.artifact_type)} requires review`,
      detail: series.latest_artifact.title || "The latest research run is waiting for review.",
      href: appPath(series.latest_artifact.deep_link),
      tone: "amber",
    });
  }
  for (const gap of workspace.gaps.items) {
    if (!gap.needs_attention) continue;
    items.push({
      id: `gap-${gap.gap_id}`,
      title: `${humanize(gap.severity)} evidence gap`,
      detail: gap.gap_text,
      href: null,
      tone: gap.severity === "urgent" ? "red" : "amber",
    });
  }
  return items;
}

function AttentionPanel({ workspace }: { workspace: CompanyWorkspaceResponse }) {
  const items = useMemo(() => attentionItems(workspace), [workspace]);
  const summary = workspace.attention;
  const attentionCoverageIncomplete = workspace.company.tracked && (
    !workspace.sections.research.available
    || !workspace.sections.gaps.available
    || (workspace.sections.monitors.authorized && !workspace.sections.monitors.available)
  );
  return (
    <Surface className="company-workspace-surface company-attention-panel" aria-labelledby="company-attention-title">
      <SurfaceHeader
        title="Needs attention"
        titleId="company-attention-title"
        count={summary.total_count}
        detail="Monitor exceptions, research awaiting review, and high-severity evidence gaps—without a synthetic thesis score."
      />
      {attentionCoverageIncomplete ? (
        <p className="company-workspace-footnote">
          One or more attention sources are temporarily unavailable; these exact totals reflect only the sections Athena could read.
        </p>
      ) : null}
      {!workspace.company.tracked ? (
        <StateMessage
          title="No Athena coverage to assess"
          detail="This provider directory profile has no Athena monitors, research history, or evidence-gap coverage."
        />
      ) : summary.total_count > 0 ? (
        <>
          <dl className="company-attention-summary">
            <div><dt>Monitor signals</dt><dd>{summary.monitor_signal_count}</dd></div>
            <div><dt>Monitor data/setup</dt><dd>{summary.monitor_setup_or_data_issue_count}</dd></div>
            <div><dt>Research reviews</dt><dd>{summary.research_review_count}</dd></div>
            <div><dt>High-priority gaps</dt><dd>{summary.high_priority_gap_count}</dd></div>
          </dl>
          <p className="company-workspace-footnote">
            A monitor can appear in both diagnostic categories; the headline total counts each review item once.
          </p>
          {items.length ? (
            <ul className="company-attention-list">
              {items.map((item) => (
                <li key={item.id}>
                  <StatusBadge tone={item.tone}>Review</StatusBadge>
                  <div>
                    <strong>{item.href ? <Link to={item.href}>{item.title}</Link> : item.title}</strong>
                    <span>{item.detail}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <StateMessage
              title={`${summary.total_count} ${summary.total_count === 1 ? "review item is" : "review items are"} outside this bounded preview`}
              detail="Open the relevant research, monitor, or evidence-gap section for the full available context."
            />
          )}
          {summary.total_count > items.length ? (
            <span className="company-workspace-bounded-note">
              Showing {items.length.toLocaleString()} of {summary.total_count.toLocaleString()} exact review items in this preview.
            </span>
          ) : null}
        </>
      ) : (
        <StateMessage
          title="No review items in available Athena evidence"
          detail="No monitor exception, review-required research run, or high-severity open evidence gap is surfaced in this bounded snapshot."
        />
      )}
    </Surface>
  );
}

function ChallengersPanel({ workspace }: { workspace: CompanyWorkspaceResponse }) {
  if (!workspace.company.tracked && workspace.challengers.items.length === 0) return null;
  return (
    <Surface className="company-workspace-surface company-challengers-panel" aria-labelledby="company-challengers-title">
      <SurfaceHeader
        title="Challenger context"
        titleId="company-challengers-title"
        count={workspace.challengers.total_count}
        detail="Active core-book peer relationships recorded for this company."
      />
      {workspace.company.tracked && !workspace.sections.challengers.available ? (
        <StateMessage
          title="Challenger context temporarily unavailable"
          detail="Athena could not read recorded challenger relationships for this snapshot. This is not an empty relationship set."
        />
      ) : workspace.challengers.items.length ? (
        <ul className="company-challenger-list">
          {workspace.challengers.items.map((challenger) => {
            const path = challenger.counterparty.company_url
              ? appPath(challenger.counterparty.company_url)
              : challenger.counterparty.ticker
                ? `/companies/${encodeURIComponent(challenger.counterparty.ticker)}`
                : null;
            return (
              <li key={challenger.relationship_id}>
                <div className="company-workspace-row-title">
                  <strong>{path
                    ? <Link to={path}>{challenger.counterparty.company_name}</Link>
                    : challenger.counterparty.company_name}</strong>
                  {challenger.rank !== null ? <StatusBadge tone="gray">Rank {challenger.rank}</StatusBadge> : null}
                </div>
                <span className="company-workspace-meta">
                  {[challenger.counterparty.ticker, humanize(challenger.relationship_type), humanize(challenger.status)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {challenger.rationale ? <p>{challenger.rationale}</p> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <StateMessage
          title="No challenger relationships"
          detail="No active core-book challenger relationship is returned for this company."
        />
      )}
      {workspace.sections.challengers.available ? <CollectionCount collection={workspace.challengers} /> : null}
    </Surface>
  );
}

function CompanyHeader({
  workspace,
  refreshing,
  onRefresh,
  canRunWorkflows,
}: {
  workspace: CompanyWorkspaceResponse;
  refreshing: boolean;
  onRefresh: () => void;
  canRunWorkflows: boolean;
}) {
  const company = workspace.company;
  const ticker = company.display_ticker || company.canonical_ticker || "Ticker unavailable";
  const context = [
    company.exchange || company.mic,
    company.country,
    company.currency,
    company.security_type ? humanize(company.security_type) : null,
    company.industry,
  ].filter(Boolean);
  return (
    <header className="company-workspace-header">
      <div>
        <Link className="company-workspace-back" to="/companies">Companies</Link>
        <span className="company-workspace-eyebrow">Company workspace</span>
        <h1><span>{ticker}</span>{" "}{company.company_name}</h1>
        <p>{context.length ? context.join(" · ") : "Listing context unavailable"}</p>
        <div className="company-workspace-header-badges">
          {company.tracked && !workspace.sections.coverage.available ? (
            <StatusBadge tone="amber">Coverage unavailable</StatusBadge>
          ) : company.tracked && workspace.coverage ? (
            <StatusBadge
              tone={workspace.coverage.status !== "active"
                ? "amber"
                : workspace.coverage.tier === "portfolio" ? "green" : "blue"}
            >
              {humanize(workspace.coverage.tier)} coverage
              {workspace.coverage.status !== "active" ? ` · ${humanize(workspace.coverage.status)}` : ""}
            </StatusBadge>
          ) : company.tracked ? (
            <StatusBadge tone="gray">No active coverage tier</StatusBadge>
          ) : (
            <StatusBadge tone="gray">Not tracked</StatusBadge>
          )}
          <StatusBadge tone="gray">{company.directory_source === "athena" ? "Athena identity" : "Provider directory"}</StatusBadge>
          {company.sector ? <StatusBadge tone="gray">{company.sector}</StatusBadge> : null}
        </div>
      </div>
      <div className="company-workspace-header-actions">
        <span>Snapshot <When value={workspace.generated_at} /></span>
        <div>
          <button className="btn" type="button" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {canRunWorkflows && company.tracked && company.canonical_ticker ? (
            <Link className="btn primary" to={`/operations?ticker=${encodeURIComponent(company.canonical_ticker)}`}>Research actions</Link>
          ) : null}
        </div>
      </div>
    </header>
  );
}

function CompanyWorkspaceView({
  workspace,
  identifier,
  refreshing,
  refresh,
}: {
  workspace: CompanyWorkspaceResponse;
  identifier: string;
  refreshing: boolean;
  refresh: () => void;
}) {
  const canReadMonitors = useCan("workflows.read");
  const canRunWorkflows = useCan("workflows.run");
  const canReadPosition = useCan("portfolio.read");
  const canWritePortfolio = useCan("portfolio.write");
  const canWriteResearch = useCan("research.write");
  return (
    <>
      <CompanyHeader
        workspace={workspace}
        refreshing={refreshing}
        onRefresh={refresh}
        canRunWorkflows={canRunWorkflows}
      />
      {!workspace.company.tracked ? (
        <div className="company-provider-caveat">
          <strong>Provider directory profile.</strong>
          <span>
            Provider freshness is not supplied, so this page does not show market cap, price, valuation, ownership, liquidity, or consensus data.
          </span>
        </div>
      ) : null}
      <AttentionPanel workspace={workspace} />
      <div className="company-workspace-layout">
        <div className="company-workspace-main">
          <ResearchPanel workspace={workspace} />
          <DocumentsPanel workspace={workspace} />
        </div>
        <aside className="company-workspace-rail" aria-label="Company context">
          <CoveragePanel
            workspace={workspace}
            identifier={identifier}
            canReadPosition={canReadPosition}
            canWritePortfolio={canWritePortfolio}
            canWriteResearch={canWriteResearch}
            canRunWorkflows={canRunWorkflows}
          />
          {workspace.company.tracked ? (
            <SourceCoveragePanel companyRef={workspace.company.issuer_id || identifier} />
          ) : null}
          <MonitorPanel workspace={workspace} canReadMonitors={canReadMonitors} />
          {workspace.company.tracked ? (
            <GapsPanel workspace={workspace} identifier={identifier} editable={canWriteResearch} />
          ) : null}
        </aside>
      </div>
      <ChallengersPanel workspace={workspace} />
      <div className="company-workspace-source-note">
        <strong>Evidence boundary</strong>
        <span>
          {workspace.provider_status.caveat} This hub summarizes bounded Athena records. It does not infer a thesis score or fill missing market, ownership, positioning, consensus, or valuation fields without source and as-of metadata.
        </span>
        {workspace.provider_status.as_of ? (
          <span>
            {workspace.company.tracked ? "Identity context as of " : "Directory lookup performed "}
            <When value={workspace.provider_status.as_of} />.
          </span>
        ) : null}
      </div>
    </>
  );
}

export default function Company() {
  const location = useLocation();
  const { companyRef: identifier = "" } = useParams();
  const detail = useQuery({
    queryKey: ["company-workspace", identifier],
    queryFn: ({ signal }) => fetchCompanyWorkspace(identifier, signal),
    retry: false,
  });

  if (detail.isLoading) {
    return (
      <div className="company-workspace-page">
        <Link className="company-workspace-back" to="/companies">Companies</Link>
        <StateMessage
          title="Loading company workspace"
          detail="Retrieving identity, evidence, research, and current operating context…"
          status
        />
      </div>
    );
  }
  if (!detail.data) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="company-workspace-page">
        <Link className="company-workspace-back" to="/companies">Companies</Link>
        <StateMessage
          title={notFound ? "Company not found" : "Company workspace unavailable"}
          detail={notFound
            ? "The company link may be outdated. Search the directory for its current listing or canonical issuer."
            : "Athena could not load this workspace. No company data is being substituted from another issuer."}
          error
          action={<button className="btn" type="button" onClick={() => void detail.refetch()}>Retry</button>}
        />
      </div>
    );
  }
  return (
    <div className="company-workspace-page" aria-busy={detail.isFetching}>
      {(location.state as { companyOnboardingNotice?: string } | null)?.companyOnboardingNotice ? (
        <div className="company-stale-warning" role="alert">
          {(location.state as { companyOnboardingNotice: string }).companyOnboardingNotice}
        </div>
      ) : null}
      {detail.isRefetchError ? (
        <div className="company-stale-warning" role="alert">
          Refresh failed. Showing the last successfully loaded snapshot from <When value={detail.data.generated_at} />.
        </div>
      ) : null}
      <CompanyWorkspaceView
        workspace={detail.data}
        identifier={identifier}
        refreshing={detail.isFetching}
        refresh={() => { void detail.refetch(); }}
      />
    </div>
  );
}
